import React, { useEffect, useState } from 'react';
import MuiAlert, { AlertColor } from '@mui/material/Alert';
import { getAdminsList, removeAdminById } from '../../../../api/users';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../../../firebase';
import { sigUpCallback } from '../../../../api/auth';
import {
  Button,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow, TextField,
  Snackbar
} from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { validateEmail } from '../../../../services/validationService';
import { useNavigate } from 'react-router-dom';

const columns = [
  { id: 'username', label: 'Username', minWidth: 170 },
  { id: 'firstname', label: 'First name', minWidth: 170 },
  { id: 'lastname', label: 'Last name', minWidth: 170 },
  { id: 'email', label: 'Email', minWidth: 170 }
];

export const AdminTableComponent = () => {
  const [adminList, setAdminList] = useState([]);
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState({
    value: '',
    isError: false
  });
  const [newAdminPassword, setNewAdminPassword] = useState({
    value: '',
    isError: false
  });
  const [firstName, setFirstName] = useState('');
  const [userName, setUserName] = useState('');
  const [lastName, setLastName] = useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [openSnackbar, setOpenSnackbar] = useState<{isOpen: boolean, alertType: AlertColor, message: string}>({
    isOpen: false,
    alertType: 'success',
    message: ''
  });
  
  useEffect(() => {
    (async () => {
      setAdminList(await getAdminsList());
    })()
  }, []);
  
  const handleAddNewUser = () => {
    setIsOpenDialog(true);
  };
  
  const handleChangeEmail = (newEmail: string) => {
    setNewAdminEmail({...newAdminEmail, value: newEmail});
  };
  
  const handleChangePassword = (newPassword: string) => {
    setNewAdminPassword({...newAdminPassword, value: newPassword});
  };
  
  const close = () => {
    setIsOpenDialog(false);
    setNewAdminPassword({ value: '', isError: false });
    setNewAdminEmail({ value: '', isError: false });
  };
  
  const createNewAdmin = async () => {
    try {
      await sigUpCallback({ role: 'ADMIN',
        email: newAdminEmail.value.toString(),
        password: newAdminPassword.value.toString(),
        username: userName,
        firstname: firstName,
        lastname: lastName
      });
      setAdminList(await getAdminsList());
      close();
      setOpenSnackbar({
        isOpen: true,
        message: "New administrator successfully added",
        alertType: "success"
      });
    } catch (e) {
      setOpenSnackbar({
        isOpen: true,
        message: "Something went wrong while creating a new administrator, please try again",
        alertType: "error"
      });
    }
  };
  
  const handleDeleteAdmin = async (value: any) => {
    try {
      await removeAdminById({firebaseId: value.firebaseId});
      setAdminList(await getAdminsList());
      setOpenSnackbar({
        isOpen: true,
        message: "Administrator successfully removed",
        alertType: "success"
      });
    } catch (e) {
      setOpenSnackbar({
        isOpen: true,
        message: "Something went wrong while deleting admin, the data was not deleted, please try again",
        alertType: "error"
      });
    }
  };
  
  return (
    <div className="mt-10">
      <div>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    style={{ minWidth: column.minWidth, fontWeight: '700', fontSize: 18 }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                <TableCell key="deleteButton" />
              </TableRow>
            </TableHead>
            <TableBody>
              {adminList.length > 0 && adminList
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  return (
                    <TableRow hover role="checkbox" tabIndex={-1} key={index.toString()}>
                      {columns.map((column) => {
                        const value = row[column.id];
                        return (
                          <TableCell key={column.id}>
                            {value || " - "}
                          </TableCell>
                        );
                      })}
                      <TableCell key="deleteButton">
                        {adminList.length > 1 && (
                          <DeleteOutlineOutlinedIcon
                            fontSize="small"
                            className="hover:cursor-pointer"
                            onClick={() => handleDeleteAdmin(row)}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        {adminList.length > 10 && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={adminList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(+event.target.value);
              setPage(0);
            }}
          />
        )}
      </div>
      <Button onClick={handleAddNewUser} className="!py-3 !px-10 !mt-10" variant="contained">
        Add new admin
      </Button>
      <Dialog open={isOpenDialog} onClose={close}>
        <DialogTitle>Create new admin</DialogTitle>
        <DialogContent>
          <DialogContentText>
            To create a new user with administrator rights, you need to specify an email and password for it.
          </DialogContentText>
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label="Username"
            name="username"
            autoComplete="username"
            variant="standard"
            autoFocus
            onChange={(e) => setUserName(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="firstName"
            label="First name"
            name="firstName"
            autoComplete="firstName"
            variant="standard"
            onChange={(e) => setFirstName(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="lastName"
            label="Last name"
            name="lastName"
            autoComplete="lastName"
            variant="standard"
            onChange={(e) => setLastName(e.target.value)}
          />
          <TextField
            error={newAdminEmail.isError}
            helperText={newAdminEmail.isError ? 'Please enter valid email': ''}
            required
            margin="normal"
            id="email"
            label="Email"
            type="email"
            fullWidth
            variant="standard"
            value={newAdminEmail.value}
            onChange={(e) => handleChangeEmail(e.target.value)}
            onBlur={() => {
              setNewAdminEmail({...newAdminEmail, isError: !validateEmail(newAdminEmail.value)})
            }}
          />
          <TextField
            error={newAdminPassword.isError}
            helperText={newAdminPassword.isError ? 'Please enter valid password': ''}
            value={newAdminPassword.value}
            required
            margin="normal"
            id="password"
            label="Password"
            type="password"
            fullWidth
            variant="standard"
            onChange={(e) => handleChangePassword(e.target.value)}
            onBlur={() => {
              setNewAdminPassword({...newAdminPassword, isError: newAdminPassword.value.length < 6})
            }}
          />
        </DialogContent>
        <DialogActions className="my-5 mx-3">
          <Button onClick={close}>Cancel</Button>
          <Button
            onClick={createNewAdmin}
            variant="contained"
            disabled={newAdminEmail.isError || newAdminPassword.value.length <= 6}
          >
            Create new admin
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={openSnackbar.isOpen}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar({...openSnackbar, isOpen: false})}
      >
        <MuiAlert
          onClose={() => setOpenSnackbar({...openSnackbar, isOpen: false})}
          severity={openSnackbar.alertType}
          sx={{ width: '100%' }}
        >
          {openSnackbar.message}
        </MuiAlert>
      </Snackbar>
    </div>
  )
}